import type {
  AuthAttempt,
  AuthCallback,
  AuthIdentity,
  AuthOrganization,
  AuthReturnTarget,
  AuthState
} from "./auth-state";
import { initialAuthState, parseAuthReturnTo, reduceAuthState } from "./auth-state";

export type AuthStartResult =
  | { readonly state: "started"; readonly attempt: AuthAttempt; readonly callback?: AuthCallback }
  | { readonly state: "cancelled" }
  | { readonly state: "unavailable"; readonly reason: string };

export type AuthVerificationResult =
  | {
    readonly state: "verified";
    readonly identity: AuthIdentity;
    readonly organizations: readonly AuthOrganization[];
  }
  | { readonly state: "refused"; readonly reason: string }
  | { readonly state: "expired"; readonly reason: string }
  | { readonly state: "unavailable"; readonly reason: string };

export interface AuthClient {
  start(returnTo: AuthReturnTarget): Promise<AuthStartResult>;
  verify(attempt: AuthAttempt, callback: AuthCallback): Promise<AuthVerificationResult>;
  signOut(): Promise<void>;
}

export class AuthFlow {
  private state: AuthState = initialAuthState;

  constructor(
    private readonly client: AuthClient,
    private readonly onTransition: (state: AuthState) => void = () => undefined
  ) {}

  private transition(event: Parameters<typeof reduceAuthState>[1]): AuthState {
    this.state = reduceAuthState(this.state, event);
    this.onTransition(this.state);
    return this.state;
  }

  snapshot(): AuthState {
    return this.state;
  }

  restore(
    identity: AuthIdentity,
    organizations: readonly AuthOrganization[],
    rawReturnTo = "/workspace?tool=inspect"
  ): AuthState {
    const returnTo = parseAuthReturnTo(rawReturnTo) ?? { route: "/workspace", tool: "inspect" };
    return this.transition({ type: "session-restored", identity, organizations, returnTo });
  }

  async signIn(rawReturnTo: string): Promise<AuthState> {
    const returnTo = parseAuthReturnTo(rawReturnTo);
    if (returnTo === null) {
      this.transition({ type: "refused", message: "The requested return target is not allowed." });
      return this.state;
    }
    const start = await this.client.start(returnTo);
    if (start.state === "cancelled") {
      this.transition({ type: "cancel" });
      return this.state;
    }
    if (start.state === "unavailable") {
      this.transition({ type: "unavailable", message: start.reason });
      return this.state;
    }
    this.transition({ type: "start", attempt: start.attempt, returnTo });
    if (start.callback === undefined) return this.state;
    this.transition({ type: "callback-received", callback: start.callback });
    if (this.state.status !== "callback-verifying") return this.state;
    const verified = await this.client.verify(start.attempt, start.callback);
    if (verified.state === "verified") {
      this.transition({
        type: "callback-verified",
        callbackId: start.callback.id,
        identity: verified.identity,
        organizations: verified.organizations
      });
    } else if (verified.state === "expired") {
      this.transition({ type: "expired", message: verified.reason });
    } else if (verified.state === "unavailable") {
      this.transition({ type: "unavailable", message: verified.reason });
    } else {
      this.transition({
        type: "refused",
        message: verified.reason,
        callbackId: start.callback.id
      });
    }
    return this.state;
  }

  selectOrganization(organizationId: string): AuthState {
    return this.transition({ type: "select-organization", organizationId });
  }

  selectProject(projectId: string): AuthState {
    return this.transition({ type: "select-project", projectId });
  }

  expire(message = "The cloud session expired. Sign in again to continue."): AuthState {
    return this.transition({ type: "expired", message });
  }

  cancel(): AuthState {
    return this.transition({ type: "cancel" });
  }

  async signOut(): Promise<AuthState> {
    await this.client.signOut();
    return this.transition({ type: "sign-out" });
  }
}
